"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { useComponentTranslations } from "./LanguageProvider";
import { useRouter } from "next/navigation";

interface RegisterPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin: () => void;
}

export default function RegisterPopup({ isOpen, onClose, onSwitchToLogin }: RegisterPopupProps) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const router = useRouter();
  const { t } = useComponentTranslations("Register");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    if (formData.password !== formData.confirmPassword) {
      setError(t("passwordsNotMatch"));
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch("/api/auth/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
        }),
      });

      if (response.ok) {
        await response.json();
        setError(t("verificationEmailSent"));
        setTimeout(() => {
          onClose();
          router.refresh();
        }, 2500);
      } else {
        const errorData = await response.json();
        if (response.status === 400 && errorData.message === "User already exists") {
          setError(t("userAlreadyExists") || "User with this email already exists");
        } else if (errorData.message === "SMTP is not configured. Registration is temporarily unavailable.") {
          setError(t("smtpNotConfigured"));
        } else {
          setError(errorData.message || t("registrationError"));
        }
      }
    } catch {
      setError(t("errorOccurred"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center px-4 glass-dark" role="dialog" aria-modal="true" aria-label="Register">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.2 }}
        className="w-full max-w-md"
      >
        <div className="glass-form p-8 relative">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">{t("title")}</h1>
            <p className="text-white/60">{t("subtitle")}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-white/80 mb-2">
                {t("name")}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 glass-input glass-rounded text-white placeholder-white/50 focus:outline-none focus:border-white/40 focus:bg-white/20 glass-transition"
                placeholder={t("namePlaceholder")}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
                {t("email")}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 glass-input glass-rounded text-white placeholder-white/50 focus:outline-none focus:border-white/40 focus:bg-white/20 glass-transition"
                placeholder={t("emailPlaceholder")}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-2">
                {t("password")}
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 glass-input glass-rounded text-white placeholder-white/50 focus:outline-none focus:border-white/40 focus:bg-white/20 glass-transition"
                placeholder={t("passwordPlaceholder")}
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-white/80 mb-2">
                {t("confirmPassword")}
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 glass-input glass-rounded text-white placeholder-white/50 focus:outline-none focus:border-white/40 focus:bg-white/20 glass-transition"
                placeholder={t("passwordPlaceholder")}
              />
            </div>

            {error && (
              <div className={`rounded-lg border p-3 ${error === t("verificationEmailSent") ? "border-emerald-500/30 bg-emerald-500/20" : "border-red-500/30 bg-red-500/20"}`}>
                <p className={`text-sm ${error === t("verificationEmailSent") ? "text-emerald-200" : "text-red-300"}`}>{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-white text-black font-medium glass-rounded glass-transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? t("signingUp") : t("signUp")}
            </button>
          </form>

          <div className="text-center mt-6">
            <p className="text-white/60">
              {t("hasAccount")}{" "}
              <button
                onClick={onSwitchToLogin}
                className="text-white hover:text-white/80 transition-colors underline"
              >
                {t("signIn")}
              </button>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
